import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { LogicalOperator } from 'constants/types';
import { useRMDispatch } from 'redux/hooks';
import { useSelectedHitButton, useUvHitsOnly } from 'redux/hooks/header';
import { ESelectedHitButton } from 'redux/reducers/headerReducer';
import { setExpHitWell } from 'redux/reducers/experimentReducer';
import { FIND_DROP_SCORES_FILTER } from './ScoringFilters/constants';
import { combineFilteredWells, FilterWell, toCombinedWellsKey } from './utils';

export interface FindDropContextType {
  selectedIngredients: string[];
  setSelectedIngredients: React.Dispatch<React.SetStateAction<string[]>>;
  ingredientFilterLogic: LogicalOperator;
  setIngredientFilterLogic: React.Dispatch<React.SetStateAction<LogicalOperator>>;
  highlightedIngredientWells: FilterWell[];
  setHighlightedIngredientWells: React.Dispatch<React.SetStateAction<FilterWell[]>>;

  scoreFilterBy: FIND_DROP_SCORES_FILTER;
  setScoreFilterBy: React.Dispatch<React.SetStateAction<FIND_DROP_SCORES_FILTER>>;

  selectedScores: number[];
  setSelectedScores: React.Dispatch<React.SetStateAction<number[]>>;
  scoreFilterLogic: LogicalOperator;
  setScoreFilterLogic: React.Dispatch<React.SetStateAction<LogicalOperator>>;
  highlightedScoreWells: FilterWell[];
  setHighlightedScoreWells: React.Dispatch<React.SetStateAction<FilterWell[]>>;
}

const FindDropContext = createContext<FindDropContextType | undefined>(undefined);

export interface FindDropProviderProps {
  children: ReactNode;
}

export const FindDropProvider: React.FC<FindDropProviderProps> = ({ children }) => {
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [ingredientFilterLogic, setIngredientFilterLogic] = useState<LogicalOperator>(
    LogicalOperator.Or,
  );
  const [highlightedIngredientWells, setHighlightedIngredientWells] = useState<
    FilterWell[]
  >([]);

  const [scoreFilterBy, setScoreFilterBy] = useState(FIND_DROP_SCORES_FILTER.BY_SCORE);
  const [selectedScores, setSelectedScores] = useState<number[]>([]);
  const [scoreFilterLogic, setScoreFilterLogic] = useState<LogicalOperator>(
    LogicalOperator.Or,
  );
  const [highlightedScoreWells, setHighlightedScoreWells] = useState<FilterWell[]>([]);

  const dispatch = useRMDispatch();
  const uvHitsOnly = useUvHitsOnly();
  const hitButton = useSelectedHitButton();
  const hitsFilterActive = hitButton !== ESelectedHitButton.None || uvHitsOnly;
  const lastDispatchedKey = useRef<string>();

  useEffect(() => {
    const isIngredientFilterActive = selectedIngredients.length > 0;
    const isScoreFilterActive =
      (scoreFilterBy === FIND_DROP_SCORES_FILTER.BY_SCORE && selectedScores.length > 0) ||
      (scoreFilterBy === FIND_DROP_SCORES_FILTER.BY_HITS && hitsFilterActive);
    const combined = combineFilteredWells(
      highlightedIngredientWells,
      highlightedScoreWells,
      isIngredientFilterActive,
      isScoreFilterActive,
    );
    const nextKey = toCombinedWellsKey(combined);
    if (lastDispatchedKey.current === nextKey) {
      return;
    }
    lastDispatchedKey.current = nextKey;
    dispatch(setExpHitWell(combined));
  }, [
    dispatch,
    highlightedIngredientWells,
    highlightedScoreWells,
    selectedIngredients,
    selectedScores,
    scoreFilterBy,
    hitsFilterActive,
  ]);

  const value = useMemo<FindDropContextType>(
    () => ({
      selectedIngredients,
      setSelectedIngredients,
      ingredientFilterLogic,
      setIngredientFilterLogic,
      highlightedIngredientWells,
      setHighlightedIngredientWells,
      scoreFilterBy,
      setScoreFilterBy,
      selectedScores,
      setSelectedScores,
      scoreFilterLogic,
      setScoreFilterLogic,
      highlightedScoreWells,
      setHighlightedScoreWells,
    }),
    [
      selectedIngredients,
      ingredientFilterLogic,
      highlightedIngredientWells,
      scoreFilterBy,
      selectedScores,
      scoreFilterLogic,
      highlightedScoreWells,
    ],
  );

  return <FindDropContext.Provider value={value}>{children}</FindDropContext.Provider>;
};

export const useFindDropContext = (): FindDropContextType => {
  const context = useContext(FindDropContext);
  if (context === undefined) {
    throw new Error('useFindDropContext must be used within a FindDropProvider');
  }
  return context;
};
